/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react"
import { useForm, Controller } from "react-hook-form"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"
import { Combobox } from "@/components/ui/custom/Selectors/Combobox"
import locationData from '../../../data/location.json'

const US_STATUSES = ["US Citizen", "Green Card", "EAD", "Valid Visa with Work Rights"] as const
const CANADA_STATUSES = ["Canadian Citizen", "Permanent Resident", "Valid Work Permit"] as const

type LocationData = {
  [country: string]: Array<{
    state?: string
    province?: string
    cities: string[]
  }>
}

interface LocationAndWorkAuthProps {
  formData: {
    location: {
      city: string
      state: string
      country: string
    }
    workAuthorization: {
      requiresSponsorship: boolean
      acceptedStatuses:
        | "Any legal work authorization"
        | {
            US?: (typeof US_STATUSES)[number][]
            Canada?: (typeof CANADA_STATUSES)[number][]
            Other?: string
          }
      requiresSecurityClearance: boolean
      requiresCitizenshipForClearance?: boolean
    }
  }
  onLocationChange: (location: { city: string; state: string; country: string }) => void
  onWorkAuthChange: (field: string, value: any) => void
}

export function LocationAndWorkAuth({ formData, onLocationChange, onWorkAuthChange }: LocationAndWorkAuthProps) {
  const [cities, setCities] = useState<string[]>([])
  const [selectedCity, setSelectedCity] = useState("")

  const { control, watch, setValue, } = useForm({
    defaultValues: formData,
  })

  useEffect(() => {
    const allCities = Object.entries(locationData as LocationData).flatMap(([country, states]) =>
      states.flatMap((state) => state.cities.map((city) => `${city}, ${state.state || state.province}, ${country}`)),
    )
    setCities(allCities)

    // Set initial selected city
    if (formData.location.city && formData.location.state && formData.location.country) {
      setSelectedCity(`${formData.location.city}, ${formData.location.state}, ${formData.location.country}`)
    }
  }, [formData.location])

  const handleCityChange = (city: string) => {
    setSelectedCity(city)
    const [cityName, state, country] = city.split(", ")
    onLocationChange({ city: cityName, state, country })
    setValue("workAuthorization.acceptedStatuses.Canada",[])
    setValue("workAuthorization.acceptedStatuses.US",[])
    setValue("workAuthorization",{
      acceptedStatuses:'Any legal work authorization',
      requiresCitizenshipForClearance:false,
      requiresSecurityClearance:false,
      requiresSponsorship:false
    })
  }

  const handleStatusChange = (country: "US" | "Canada", status: string, checked: boolean) => {
    console.log('attempting to toggle...')
    if (typeof formData.workAuthorization.acceptedStatuses === "string") {
      return
    }

    const currentStatuses = formData.workAuthorization.acceptedStatuses[country] || []
    const newStatuses = checked ? [...currentStatuses, status] : currentStatuses.filter((s) => s !== status)

    onWorkAuthChange("acceptedStatuses", {
      ...formData.workAuthorization.acceptedStatuses,
      [country]: newStatuses,
    })
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="city">Location</Label>
        <Controller
          name="location.city"
          control={control}
          render={({ field }) => (
            <Combobox
              //id="city"
              items={cities}
              value={selectedCity}
              onChange={(value) => {
                field.onChange(value)
                handleCityChange(value)
              }}
              placeholder="Search for a city"
            />
          )}
        />
        {!watch("location.city") && <p className="text-sm text-red-500">Required location</p>}
      </div>

      {(watch("location.country") || formData.location.country) && (
        <div className="space-y-4">
          <h3 className="font-medium text-lg">Work Authorization Requirements</h3>

          <div className="flex items-center justify-between">
            <Label htmlFor="requiresSponsorship">Requires Visa Sponsorship</Label>
            <Controller
              name="workAuthorization.requiresSponsorship"
              control={control}
              render={({ field }) => (
                <Switch
                  id="requiresSponsorship"
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked)
                    onWorkAuthChange("requiresSponsorship", checked)
                  }}
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>Accepted Work Authorization Status</Label>
            <Controller
              name="workAuthorization.acceptedStatuses"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={typeof field.value === "string" ? "any" : "specific"}
                  onValueChange={(value) => {
                    const newValue = value === "any" ? "Any legal work authorization" : {}
                    field.onChange(newValue)
                    onWorkAuthChange("acceptedStatuses", newValue)
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="any" id="any-status" />
                    <Label htmlFor="any-status">Accept any legal work authorization</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="specific" id="specific-status" />
                    <Label htmlFor="specific-status">Specify accepted statuses</Label>
                  </div>
                </RadioGroup>
              )}
            />

            {typeof watch("workAuthorization.acceptedStatuses") === "object" && (
              <div className="space-y-4 mt-4">
                {formData.location.country === "United States" && (
                  <div className="space-y-2">
                    <Label>US Work Authorization</Label>
                    <div className="grid gap-2">
                      {US_STATUSES.map((status) => (
                        <div key={status} className="flex items-center space-x-2">
                          <Controller
                            name={`workAuthorization.acceptedStatuses.US`}
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                id={`us-status-${status}`}
                                checked={field.value?.includes(status)}
                                onCheckedChange={(checked) => {
                                  // Force immediate update
                                  field.onChange(checked ? 
                                    [...(field.value || []), status] : 
                                    (field.value || []).filter((s: string) => s !== status)
                                  )
                                  handleStatusChange("US", status, !!checked.valueOf())
                                }}
                              />
                            )}
                          />
                          <Label htmlFor={`us-status-${status}`}>{status}</Label>
                        </div>
                      ))}
                      {!watch("workAuthorization.acceptedStatuses.US") && <p className="text-sm text-red-500">Required Work Authorization</p>}
                    </div>
                  </div>
                )}

                {formData.location.country === "Canada" && (
                  <div className="space-y-2">
                    <Label>Canada Work Authorization</Label>
                    <div className="grid gap-2">
                      {CANADA_STATUSES.map((status) => (
                        <div key={status} className="flex items-center space-x-2">
                          <Controller
                            name={`workAuthorization.acceptedStatuses.Canada`}
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                id={`ca-status-${status}`}
                                checked={field.value?.includes(status)}
                                onCheckedChange={(checked) => {
                                              // Force immediate update
                                field.onChange(checked ? 
                                  [...(field.value || []), status] : 
                                  (field.value || []).filter((s: string) => s !== status)
                                )
                                  handleStatusChange("Canada", status, !!checked.valueOf())
                                }}
                              />
                            )}
                          />
                          <Label htmlFor={`ca-status-${status}`}>{status}</Label>
                        </div>
                      ))}
                      {!watch("workAuthorization.acceptedStatuses.Canada") && <p className="text-sm text-red-500">Required Work Authorization</p>}
                    </div>
                  </div>
                )}

                {formData.location.country !== "United States" && formData.location.country !== "Canada" && (
                  <div className="space-y-2">
                    <Label htmlFor="other-status">Other Countries Requirements</Label>
                    <Controller
                      name="workAuthorization.acceptedStatuses.Other"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          id="other-status"
                          {...field}
                          placeholder="Specify work authorization requirements for this country..."
                          onChange={(e) => {
                            field.onChange(e)
                            onWorkAuthChange("acceptedStatuses", {
                              ...watch("workAuthorization.acceptedStatuses") as any,
                              Other: e.target.value,
                            })
                          }}
                        />
                      )}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="requiresSecurityClearance">Requires Security Clearance</Label>
              <Controller
                name="workAuthorization.requiresSecurityClearance"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="requiresSecurityClearance"
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked)
                      onWorkAuthChange("requiresSecurityClearance", checked)
                      if (!checked) {
                        onWorkAuthChange("requiresCitizenshipForClearance", false)
                      }
                    }}
                  />
                )}
              />
            </div>

            {watch("workAuthorization.requiresSecurityClearance") && (
              <div className="flex items-center justify-between">
                <Label htmlFor="requiresCitizenshipForClearance">Requires Citizenship for Clearance</Label>
                <Controller
                  name="workAuthorization.requiresCitizenshipForClearance"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id="requiresCitizenshipForClearance"
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked)
                        onWorkAuthChange("requiresCitizenshipForClearance", checked)
                      }}
                    />
                  )}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

